// BUILD-046 task 8.16 (supporting model): Take task on the Group / Shared view (design "Group / Shared view and Take task",
// "Sequence: Take task including a concurrent claim"; Req 4.5 - 4.9, 4.25, 4.26, 7.3 - 7.5, 7.10).
//
// Take task is a per-row state machine:  idle -> taking -> (row leaves the queue) | failed | lost.
//  - `activateTake` is pure and synchronous (the click handler calls it before it awaits), so the control is disabled at once and a second
//    activation while the request runs sends nothing.
//  - A LOST claim (409 ALREADY_CLAIMED) shows WHO took it, from the 409 payload (`details.owner.displayName`), as a notice that stays after the
//    list reloads and the row is gone, and the list is reloaded. It is not shown as a generic failure.
//  - Any other failure says the task was not taken, with the server's reason. An answer that never came (timeout, no connection) says the
//    result could not be confirmed, because the claim may have gone through; the task is not shown as taken or as free.
//
// Who is offered Take task: only a row of the Group / Shared view that is an unclaimed, open, readable Shared_Task. The list the server
// returns for that view holds only the Caller's Queue_Eligible tasks, so this reflects the rule and the server decides on the claim
// (eligibility is re-checked at claim time, Req 4.16). The Oversight and Team views never offer it, and an overseer's view of someone
// else's task never adds anything to the overseer's own lists (Req 7.7, 14.19).
//
// Pure module: no React, no I/O; its only runtime import is the pure display helper displayNames.ts (D1: a person is written First name + last initial, without the
// "(FICTIONAL)" test marker). Erasable TypeScript only (run by `node --experimental-strip-types`).

import { personDisplay } from './displayNames';

export type TakePhase = 'idle' | 'taking' | 'failed' | 'lost';

export interface TakeFailure {
  /** Always says the task was not taken (Req 4.9). */
  headline: string;
  detail: string | null;
}

export interface TakeItemState {
  phase: TakePhase;
  failure: TakeFailure | null;
  /** The display name of whoever took it first (phase `lost`). */
  takenBy: string | null;
}

export interface TakeNotice {
  /** The task the notice is about. */
  id: string;
  text: string;
}

export interface TakeState {
  readonly items: Readonly<Record<string, TakeItemState>>;
  /** Lost-claim notices; they outlive the row, which leaves the list on the reload. */
  readonly notices: readonly TakeNotice[];
}

const IDLE: TakeItemState = Object.freeze({ phase: 'idle', failure: null, takenBy: null }) as TakeItemState;

export const NOT_TAKEN = 'The task was not taken.';

export function initialTakeState(): TakeState {
  return { items: {}, notices: [] };
}

export function itemTake(state: TakeState, id: string): TakeItemState {
  return state.items[id] ?? IDLE;
}

function put(state: TakeState, id: string, next: TakeItemState, notices: readonly TakeNotice[] = state.notices): TakeState {
  return { items: { ...state.items, [id]: next }, notices };
}

// ───────────────────────── who is offered Take task ─────────────────────────

export type TakeListView = 'my-tasks' | 'shared' | 'oversight' | 'team';

export interface TakeRow {
  status?: string;
  /** Present on a claimed task. */
  ownerEmployeeId?: string;
  unclaimed?: boolean;
  redacted?: boolean;
  lifecycle?: string;
  sourceGoverned?: boolean;
}

/** `take` only for an unclaimed, open, readable Shared_Task on the Group / Shared view. */
export function takeControl(row: TakeRow, view: TakeListView): 'take' | 'none' {
  if (view !== 'shared') return 'none';
  if (row.unclaimed !== true) return 'none';
  if (typeof row.ownerEmployeeId === 'string' && row.ownerEmployeeId !== '') return 'none';
  if (row.status !== 'active' && row.status !== 'in_progress' && row.status !== 'waiting' && row.status !== 'blocked') return 'none';
  if (row.redacted === true || row.lifecycle === 'cancelled') return 'none';
  return 'take';
}

// ───────────────────────── Take task ─────────────────────────

/** The click handler: `send` is true exactly when the caller must now send the claim. */
export function activateTake(state: TakeState, id: string): { state: TakeState; send: boolean } {
  const current = itemTake(state, id);
  if (current.phase === 'taking') return { state, send: false };
  const notices = state.notices.filter((n) => n.id !== id);
  return { state: put(state, id, { phase: 'taking', failure: null, takenBy: null }, notices), send: true };
}

/** The claim was accepted: the row is gone from the queue (the caller merges the item into My tasks and reloads). Applies to a claim in progress. */
export function takeSucceeded(state: TakeState, id: string): TakeState {
  if (itemTake(state, id).phase !== 'taking') return state;
  const items = { ...state.items };
  delete items[id];
  return { items, notices: state.notices };
}

interface ThrownLike {
  kind?: unknown;
  status?: unknown;
  code?: unknown;
  message?: unknown;
  details?: unknown;
}

function asThrown(thrown: unknown): ThrownLike {
  return thrown !== null && typeof thrown === 'object' ? (thrown as ThrownLike) : {};
}

/** The display name of the owner in a 409 payload, or null. */
export function ownerNameFrom(thrown: unknown): string | null {
  const details = asThrown(thrown).details;
  if (details !== null && typeof details === 'object') {
    const owner = (details as { owner?: unknown }).owner;
    if (owner !== null && typeof owner === 'object') {
      const name = (owner as { displayName?: unknown }).displayName;
      if (typeof name === 'string' && name.trim() !== '') return name.trim();
    }
  }
  return null;
}

export function isLostClaim(thrown: unknown): boolean {
  const t = asThrown(thrown);
  return t.status === 409 && t.code === 'ALREADY_CLAIMED';
}

/** The text for a lost claim: who took it, from the payload; without a name, the server's own message; else a plain statement. */
export function lostClaimText(thrown: unknown): string {
  const name = ownerNameFrom(thrown);
  if (name !== null) return `Already taken by ${personDisplay(name).short || name}.`;
  const message = asThrown(thrown).message;
  return typeof message === 'string' && message.trim() !== '' ? message.trim() : 'Already taken by someone else.';
}

export function describeTakeFailure(thrown: unknown): TakeFailure {
  const t = asThrown(thrown);
  if (t.kind === 'timeout' || t.kind === 'network') {
    return { headline: NOT_TAKEN, detail: 'We could not confirm the result. Reload to see whether it is now yours.' };
  }
  const message = typeof t.message === 'string' && t.message.trim() !== '' ? t.message.trim() : null;
  return { headline: NOT_TAKEN, detail: message };
}

/** Whether the Group / Shared list is read again after this failure: the task is no longer (or never was) takeable. */
export function reloadAfterTakeFailure(thrown: unknown): boolean {
  const t = asThrown(thrown);
  return t.status === 409 || t.status === 403 || t.status === 404;
}

/**
 * The claim failed. A lost claim becomes a `lost` row plus a notice naming who took it (`title` is the task title as listed); anything
 * else becomes a `failed` row. An answer dropped because the Caller changed changes nothing.
 */
export function takeFailed(state: TakeState, id: string, thrown: unknown, title: string): TakeState {
  if (itemTake(state, id).phase !== 'taking') return state;
  if (asThrown(thrown).kind === 'stale') return state;
  if (isLostClaim(thrown)) {
    const text = lostClaimText(thrown);
    const notice: TakeNotice = { id, text: title.trim() === '' ? text : `${title.trim()}: ${text}` };
    return put(state, id, { phase: 'lost', failure: null, takenBy: ownerNameFrom(thrown) }, [...state.notices.filter((n) => n.id !== id), notice]);
  }
  return put(state, id, { phase: 'failed', failure: describeTakeFailure(thrown), takenBy: null });
}

export function dismissTakeNotice(state: TakeState, id: string): TakeState {
  if (!state.notices.some((n) => n.id === id)) return state;
  return { items: state.items, notices: state.notices.filter((n) => n.id !== id) };
}

/** A Caller change: nothing that belongs to the previous Caller's session (a saving state, a notice) survives. */
export function resetTakeState(): TakeState {
  return initialTakeState();
}

// ───────────────────────── what the row shows ─────────────────────────

export interface TakeControlView {
  visible: boolean;
  disabled: boolean;
  label: 'Take task' | 'Taking…';
  /** `Already taken by <name>.` on a lost row. */
  lost: string | null;
  failure: TakeFailure | null;
}

export function describeTake(state: TakeState, id: string, row: TakeRow, view: TakeListView): TakeControlView {
  const item = itemTake(state, id);
  const offered = takeControl(row, view) === 'take';
  return {
    visible: offered,
    disabled: item.phase === 'taking' || item.phase === 'lost',
    label: item.phase === 'taking' ? 'Taking…' : 'Take task',
    lost: item.phase === 'lost' ? (item.takenBy !== null ? `Already taken by ${personDisplay(item.takenBy).short || item.takenBy}.` : 'Already taken by someone else.') : null,
    failure: item.phase === 'failed' ? item.failure : null,
  };
}

// ───────────────────────── the flow the component runs ─────────────────────────

export interface TakeApi {
  claim: (id: string) => Promise<{ item: unknown }>;
}

export interface TakeStore {
  get: () => TakeState;
  set: (next: TakeState) => void;
}

export type TakeOutcome =
  | { kind: 'ignored' }
  | { kind: 'taken'; item: unknown }
  | { kind: 'lost'; reload: true }
  | { kind: 'failed'; reload: boolean }
  | { kind: 'dropped' };

/**
 * Take task for one row. The state changes before the request is sent; a repeat activation while it runs sends nothing. The caller acts on
 * the outcome: `taken` merges the item into My tasks and reloads the queue; `lost` and a failure with `reload` reload the queue.
 */
export async function performTake(api: TakeApi, store: TakeStore, id: string, title: string): Promise<TakeOutcome> {
  const step = activateTake(store.get(), id);
  if (!step.send) return { kind: 'ignored' };
  store.set(step.state);
  try {
    const response = await api.claim(id);
    store.set(takeSucceeded(store.get(), id));
    return { kind: 'taken', item: response.item };
  } catch (thrown) {
    if (asThrown(thrown).kind === 'stale') return { kind: 'dropped' };
    store.set(takeFailed(store.get(), id, thrown, title));
    if (isLostClaim(thrown)) return { kind: 'lost', reload: true };
    return { kind: 'failed', reload: reloadAfterTakeFailure(thrown) };
  }
}
